import { useQueries } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type {
  AnalyticsFilters,
  ModuleAnalyticsResponse,
  AnalyticsDataPoint,
} from "./useGetModuleAnalytics";
import { parseAnalyticsData } from "./useGetModuleAnalytics";

export interface GroupAnalyticsEntry {
  groupId: number;
  groupName: string;
  timeSeries: AnalyticsDataPoint[];
  total: number;
  unit: string;
}

type GroupKey = "site_id" | "bu_id" | "activite_id" | "filiere_id" | "region_id";

const GROUP_KEY_TO_PLURAL: Record<GroupKey, string> = {
  site_id: "site_ids",
  bu_id: "bu_ids",
  activite_id: "activite_ids",
  filiere_id: "filiere_ids",
  region_id: "region_ids",
};

interface UseGetPerGroupAnalyticsOptions {
  baseFilters: Partial<AnalyticsFilters>;
  groupKey: GroupKey;
  groupIds: number[];
  groupNames: Record<number, string>;
  module: "eau" | "energie";
  enabled?: boolean;
}

/**
 * Generic hook to fetch analytics broken down by any organizational dimension.
 * Calls the API once per group ID (in parallel) and returns per-group data.
 * Works like useGetPerSiteAnalytics but for any dimension (BU, Activité, Filière, etc.)
 */
export const useGetPerGroupAnalytics = ({
  baseFilters,
  groupKey,
  groupIds,
  groupNames,
  module,
  enabled = true,
}: UseGetPerGroupAnalyticsOptions) => {
  const results = useQueries({
    queries: groupIds.map((groupId) => ({
      queryKey: ["perGroupAnalytics", module, groupKey, groupId, baseFilters],
      queryFn: async (): Promise<GroupAnalyticsEntry> => {
        const filters: Record<string, unknown> = {};
        Object.entries(baseFilters).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            filters[key] = value;
          }
        });
        filters[GROUP_KEY_TO_PLURAL[groupKey]] = [groupId];

        const endpoint =
          module === "energie" ? `/core/analytics/energie/` : `/core/saisie-analytics/`;

        const response = await fetchClient<ModuleAnalyticsResponse>(endpoint, {
          method: "POST",
          body: filters,
        });

        if (response.error) {
          return {
            groupId,
            groupName: groupNames[groupId] || `Group ${groupId}`,
            timeSeries: [],
            total: 0,
            unit: "",
          };
        }

        const { total, unit, timeSeries } = parseAnalyticsData(response.data);
        return {
          groupId,
          groupName: groupNames[groupId] || `Group ${groupId}`,
          timeSeries,
          total,
          unit,
        };
      },
      enabled: enabled && !!baseFilters.champ_metier && groupIds.length > 0,
      staleTime: 1000 * 60 * 5,
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const hasError = results.some((r) => r.isError);
  const groupData: GroupAnalyticsEntry[] = results
    .map((r) => r.data)
    .filter((d): d is GroupAnalyticsEntry => !!d && d.total > 0)
    .sort((a, b) => b.total - a.total);

  const allMonths = Array.from(
    new Set(groupData.flatMap((g) => g.timeSeries.map((t) => t.month)))
  ).sort();

  return { groupData, allMonths, isLoading, hasError };
};
